import { ORG_CONTEXT_REJECTION_CODES } from '@cmoh/shared'
import { z } from 'zod'

/**
 * Per-tab organization store. Each browser tab pins the organization it shows in sessionStorage
 * and sends it on every API request, so tabs on different organizations never cross-write.
 * Resolution at load: `?org=` → sessionStorage → the session's active organization.
 *
 * `?org=` stays in the URL so links, bookmarks and new tabs keep the organization. When the API
 * rejects an organization that came from the URL, the tab shows it as forbidden instead of
 * silently switching: the link pointed at that organization on purpose.
 */

const TAB_ORGANIZATION_STORAGE_KEY = 'referent.tab-organization.v1'
const REJECTED_TAB_ORGANIZATION_STORAGE_KEY = 'referent.tab-organization.rejected.v1'
export const ENTRY_PATH = '/'
const NEW_TAB_TARGET = '_blank'
const NEW_TAB_FEATURES = 'noopener,noreferrer'
const SEARCH_PREFIX = '?'
export const TAB_ORGANIZATION_QUERY_PARAM = 'org'

const TAB_ORGANIZATION_SOURCE = {
  URL: 'url',
  STORAGE: 'storage',
  SESSION: 'session',
} as const

type TabOrganizationSource = (typeof TAB_ORGANIZATION_SOURCE)[keyof typeof TAB_ORGANIZATION_SOURCE]

export const ORGANIZATION_IN_URL_POLICY = {
  UNKNOWN: 'unknown',
  CARRY: 'carry',
  OMIT: 'omit',
} as const

export type OrganizationInUrlPolicy =
  (typeof ORGANIZATION_IN_URL_POLICY)[keyof typeof ORGANIZATION_IN_URL_POLICY]

const organizationIdSchema = z.guid()
const rejectionCodes: ReadonlySet<string> = new Set(ORG_CONTEXT_REJECTION_CODES)
const listeners = new Set<() => void>()

let tabOrganizationId: string | null = null
let tabOrganizationSource: TabOrganizationSource | null = null
let isUrlOrganizationRejected = false
let isRecovering = false
let isPinningSuspended = false
let organizationInUrlPolicy: OrganizationInUrlPolicy = ORGANIZATION_IN_URL_POLICY.UNKNOWN
let hasPendingOrganizationChangeNotice = false

function isOrganizationId(value: string | null | undefined): value is string {
  return organizationIdSchema.safeParse(value).success
}

function readStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    return
  }
}

function removeStorage(key: string): void {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    return
  }
}

function notifyListeners(): void {
  for (const listener of listeners) listener()
}

export function subscribeToTabOrganization(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** An empty `?org=` counts as absent. */
export function readOrganizationParam(search: string): string | null {
  const value = new URLSearchParams(search).get(TAB_ORGANIZATION_QUERY_PARAM)
  return value === null || value === '' ? null : value
}

function rejectUrlOrganization(): void {
  if (isUrlOrganizationRejected) return
  isUrlOrganizationRejected = true
  hasPendingOrganizationChangeNotice = false
  removeStorage(TAB_ORGANIZATION_STORAGE_KEY)
  notifyListeners()
}

export function initializeTabOrganization(): void {
  const requested = readOrganizationParam(window.location.search)
  const stored = readStorage(TAB_ORGANIZATION_STORAGE_KEY)
  const storedOrganizationId = isOrganizationId(stored) ? stored : null
  if (requested === null) {
    tabOrganizationId = storedOrganizationId
    tabOrganizationSource = storedOrganizationId === null ? null : TAB_ORGANIZATION_SOURCE.STORAGE
    return
  }
  tabOrganizationSource = TAB_ORGANIZATION_SOURCE.URL
  if (!isOrganizationId(requested)) {
    rejectUrlOrganization()
    return
  }
  hasPendingOrganizationChangeNotice =
    storedOrganizationId !== null && storedOrganizationId !== requested
  setTabOrganizationId(requested)
}

export function getTabOrganizationId(): string | null {
  return tabOrganizationId
}

export function getIsUrlOrganizationRejected(): boolean {
  return isUrlOrganizationRejected
}

export function setTabOrganizationId(organizationId: string): void {
  tabOrganizationId = organizationId
  writeStorage(TAB_ORGANIZATION_STORAGE_KEY, organizationId)
  removeStorage(REJECTED_TAB_ORGANIZATION_STORAGE_KEY)
  notifyListeners()
}

/**
 * `untilReload` blocks re-pinning until the page unloads, so a session fetch still in flight from
 * the previous identity cannot pin its org back before a full-page redirect.
 */
export function clearTabOrganization({
  untilReload = false,
}: {
  untilReload?: boolean
} = {}): void {
  isPinningSuspended = untilReload
  tabOrganizationId = null
  tabOrganizationSource = null
  organizationInUrlPolicy = ORGANIZATION_IN_URL_POLICY.UNKNOWN
  removeStorage(TAB_ORGANIZATION_STORAGE_KEY)
  removeStorage(REJECTED_TAB_ORGANIZATION_STORAGE_KEY)
  notifyListeners()
}

export function pinTabOrganization(sessionOrganizationId: string | null | undefined): void {
  if (isPinningSuspended || isUrlOrganizationRejected || tabOrganizationId !== null) return
  if (!isOrganizationId(sessionOrganizationId)) return
  if (readStorage(REJECTED_TAB_ORGANIZATION_STORAGE_KEY) === sessionOrganizationId) return
  tabOrganizationId = sessionOrganizationId
  tabOrganizationSource = TAB_ORGANIZATION_SOURCE.SESSION
  writeStorage(TAB_ORGANIZATION_STORAGE_KEY, sessionOrganizationId)
  notifyListeners()
}

/** A reload that keeps the rejected org in `?org=` would come back as a URL org and show Forbidden. */
function reloadWithoutOrganizationParam(): void {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(TAB_ORGANIZATION_QUERY_PARAM)) {
    window.location.reload()
    return
  }
  url.searchParams.delete(TAB_ORGANIZATION_QUERY_PARAM)
  window.location.replace(url.toString())
}

export function recoverFromRejectedTabOrganization(
  sentOrganizationId: string,
  errorCode: string | undefined,
): void {
  if (isRecovering || sentOrganizationId !== tabOrganizationId) return
  if (errorCode === undefined || !rejectionCodes.has(errorCode)) return
  if (tabOrganizationSource === TAB_ORGANIZATION_SOURCE.URL) {
    rejectUrlOrganization()
    return
  }
  isRecovering = true
  tabOrganizationId = null
  removeStorage(TAB_ORGANIZATION_STORAGE_KEY)
  writeStorage(REJECTED_TAB_ORGANIZATION_STORAGE_KEY, sentOrganizationId)
  reloadWithoutOrganizationParam()
}

/** Full load on `/` without `?org=`, so the tab falls back to the session's organization. */
export function leaveRejectedTabOrganization(): void {
  clearTabOrganization()
  window.location.assign(ENTRY_PATH)
}

export function setOrganizationInUrlPolicy(policy: OrganizationInUrlPolicy): void {
  if (organizationInUrlPolicy === policy) return
  organizationInUrlPolicy = policy
  notifyListeners()
}

/** The org that links carry: everyone but known single-org users, also while the policy loads. */
export function getUrlOrganizationId(): string | null {
  if (isUrlOrganizationRejected) return null
  if (organizationInUrlPolicy === ORGANIZATION_IN_URL_POLICY.OMIT) return null
  return tabOrganizationId
}

/** The org the address bar is forced to show: only once the user is known to need it. */
export function getEnforcedUrlOrganizationId(): string | null {
  if (organizationInUrlPolicy !== ORGANIZATION_IN_URL_POLICY.CARRY) return null
  return getUrlOrganizationId()
}

export function consumeOrganizationChangeNotice(): boolean {
  const hasNotice = hasPendingOrganizationChangeNotice
  hasPendingOrganizationChangeNotice = false
  return hasNotice
}

export function appendOrganizationParam(
  search: string | undefined,
  organizationId: string,
): string {
  const searchParams = new URLSearchParams(search)
  searchParams.set(TAB_ORGANIZATION_QUERY_PARAM, organizationId)
  return `${SEARCH_PREFIX}${searchParams.toString()}`
}

export function buildOrganizationEntryPath(organizationId: string): string {
  return `${ENTRY_PATH}${appendOrganizationParam(undefined, organizationId)}`
}

export function openOrganizationInNewTab(organizationId: string): void {
  window.open(buildOrganizationEntryPath(organizationId), NEW_TAB_TARGET, NEW_TAB_FEATURES)
}
